// C11 — Shared With Me dashboard (route /shared-with-me).
//
// Two sections:
//   • Pending Invitations — from GET /api/invitations/me (C6)
//   • Shared with me      — from GET /api/me/shared-resources (C11)
//
// Polished empty states for each. No backend mutations on this page —
// it's a read surface; accept/decline lives on /invitation/{token}.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Layers, Inbox, Loader2, Mailbox, ArrowRight, Eye, Pencil,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import { sharedApi, timeAgo } from '../lib/notificationsApi';

export default function SharedWithMePage() {
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [accepted, setAccepted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, a] = await Promise.allSettled([
        sharedApi.pendingInvitations(),
        sharedApi.sharedResources(),
      ]);
      if (p.status === 'fulfilled') setPending(p.value?.pending || []);
      if (a.status === 'fulfilled') setAccepted(a.value?.accepted || []);
      if (p.status === 'rejected' && a.status === 'rejected') {
        setError('Could not load your shared workspace.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-8" data-testid="shared-with-me-page">
        <h1 className="text-xl font-bold text-gray-900 mb-1">Shared with me</h1>
        <p className="text-sm text-gray-500 mb-6">
          Invitations awaiting your response, and resources you've already joined.
        </p>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        )}

        {!loading && error && (
          <p className="text-sm text-red-600 py-4" data-testid="page-error">{error}</p>
        )}

        {!loading && !error && (
          <>
            {/* ── Pending Invitations ───────────────────────────────── */}
            <Section
              title="Pending invitations"
              count={pending.length}
              testId="section-pending"
            >
              {pending.length === 0 ? (
                <EmptyState
                  icon={Mailbox}
                  title="No pending invitations"
                  body="When someone invites you to collaborate, the link will appear here."
                />
              ) : (
                <ul className="grid gap-3 sm:grid-cols-2" data-testid="pending-list">
                  {pending.map((inv) => (
                    <PendingInviteCard
                      key={inv.invite_token}
                      inv={inv}
                      onOpen={() => navigate(`/invitation/${inv.invite_token}`)}
                    />
                  ))}
                </ul>
              )}
            </Section>

            {/* ── Shared with me ───────────────────────────────────── */}
            <Section
              title="Shared with me"
              count={accepted.length}
              testId="section-accepted"
              className="mt-8"
            >
              {accepted.length === 0 ? (
                <EmptyState
                  icon={Inbox}
                  title="No shared resources yet"
                  body="Designs and templates shared with you will appear here once you accept an invitation."
                />
              ) : (
                <ul className="grid gap-3 sm:grid-cols-2" data-testid="accepted-list">
                  {accepted.map((row) => (
                    <SharedResourceCard
                      key={row.collaborator_id}
                      row={row}
                      onOpen={() => {
                        if (row.resource_type === 'design') navigate(`/editor/${row.resource_id}`);
                        else navigate(`/template/${row.resource_id}`);
                      }}
                    />
                  ))}
                </ul>
              )}
            </Section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}


// ── Subcomponents ───────────────────────────────────────────────────────
function Section({ title, count, children, testId, className = '' }) {
  return (
    <section className={className} data-testid={testId}>
      <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        {title}
        <span className="text-xs text-gray-400 font-normal">({count})</span>
      </h2>
      {children}
    </section>
  );
}

function PendingInviteCard({ inv, onOpen }) {
  const ResourceIcon = inv.resource_type === 'template' ? Layers : FileText;
  return (
    <li
      className="bg-white border border-gray-100 rounded-lg p-4 hover:border-indigo-200
                 hover:shadow-md cursor-pointer transition-all"
      onClick={onOpen}
      data-testid={`pending-card-${inv.invite_token}`}
    >
      <div className="flex items-start gap-3">
        <span className="flex-shrink-0 w-10 h-10 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center">
          <ResourceIcon className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
            {inv.resource_type}
          </p>
          <p className="text-sm font-medium text-gray-900 truncate">
            {inv.resource_name || 'Untitled'}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {inv.owner_name || 'A collaborator'} · {inv.role}
          </p>
          {inv.expires_at && (
            <p className="text-[11px] text-amber-600 mt-1.5">
              Expires {timeAgo(inv.expires_at).replace(' ago', '')} from now
            </p>
          )}
        </div>
        <ArrowRight className="w-4 h-4 text-gray-300 flex-shrink-0 mt-1" />
      </div>
    </li>
  );
}

function SharedResourceCard({ row, onOpen }) {
  const ResourceIcon = row.resource_type === 'template' ? Layers : FileText;
  const RoleIcon = row.role === 'viewer' ? Eye : Pencil;
  const openLabel = row.role === 'viewer' ? 'View' : 'Open';
  return (
    <li
      className="bg-white border border-gray-100 rounded-lg p-4 hover:border-indigo-200
                 hover:shadow-md cursor-pointer transition-all flex flex-col"
      onClick={onOpen}
      data-testid={`accepted-card-${row.collaborator_id}`}
    >
      <div className="flex items-start gap-3">
        <span className="flex-shrink-0 w-10 h-10 rounded-md bg-gray-100 text-gray-600 flex items-center justify-center">
          <ResourceIcon className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
            {row.resource_type}
          </p>
          <p className="text-sm font-medium text-gray-900 truncate">
            {row.resource_name || 'Untitled'}
          </p>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            Owner: {row.owner_name || row.owner_email || 'Unknown'}
          </p>
        </div>
        <span
          className={[
            'flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium',
            row.role === 'viewer'
              ? 'bg-gray-100 text-gray-700'
              : 'bg-indigo-50 text-indigo-700',
          ].join(' ')}
          data-testid={`role-pill-${row.collaborator_id}`}
        >
          <RoleIcon className="w-3 h-3" />
          {row.role}
        </span>
      </div>
      <div className="mt-4 flex items-center justify-end">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
          data-testid={`open-${row.collaborator_id}`}
        >
          {openLabel} {row.resource_type === 'template' ? 'Template' : 'Design'}
          <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
        </Button>
      </div>
    </li>
  );
}

function EmptyState({ icon: Icon, title, body }) {
  return (
    <div
      className="bg-white border border-dashed border-gray-200 rounded-lg
                 text-center py-10 px-6"
      data-testid="section-empty"
    >
      <Icon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
      <p className="text-sm text-gray-700 font-medium">{title}</p>
      <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">{body}</p>
    </div>
  );
}
